import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const KnowledgeBase = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'Tout', icon: 'List' },
    { id: 'getting-started', name: 'Démarrage', icon: 'Play' },
    { id: 'commissions', name: 'Commissions', icon: 'DollarSign' },
    { id: 'marketing', name: 'Marketing', icon: 'Megaphone' },
    { id: 'technical', name: 'Technique', icon: 'Settings' }
  ];

  const resources = [
    {
      id: 1,
      title: 'Guide de démarrage rapide',
      description: 'Apprenez les bases pour commencer à utiliser Artis.io efficacement',
      category: 'getting-started',
      type: 'guide',
      duration: '5 min',
      isPopular: true,
      icon: 'BookOpen'
    },
    {
      id: 2,
      title: 'Comment calculer vos commissions',
      description: 'Comprendre le système de calcul des commissions et les différents modèles',
      category: 'commissions',
      type: 'tutorial',
      duration: '8 min',
      isPopular: true,
      icon: 'Calculator'
    },
    {
      id: 3,
      title: 'Créer des liens d\'affiliation efficaces',
      description: 'Meilleures pratiques pour générer et utiliser vos liens de suivi',
      category: 'marketing',
      type: 'video',
      duration: '12 min',
      isPopular: false,
      icon: 'Link'
    },
    {
      id: 4,
      title: 'Optimiser vos campagnes marketing',
      description: 'Stratégies pour améliorer vos taux de conversion et performances',
      category: 'marketing',
      type: 'guide',
      duration: '15 min',
      isPopular: true,
      icon: 'TrendingUp'
    },
    {
      id: 5,
      title: 'Intégration API et webhooks',
      description: 'Documentation technique pour les intégrations avancées',
      category: 'technical',
      type: 'documentation',
      duration: '20 min',
      isPopular: false,
      icon: 'Code'
    },
    {
      id: 6,
      title: 'Résoudre les problèmes courants',
      description: 'Solutions aux problèmes fréquemment rencontrés par les partenaires',
      category: 'technical',
      type: 'faq',
      duration: '10 min',
      isPopular: false,
      icon: 'HelpCircle'
    }
  ];

  const filteredResources = resources?.filter(resource => {
    const matchesCategory = selectedCategory === 'all' || resource?.category === selectedCategory;
    const matchesSearch = resource?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         resource?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const getTypeIcon = (type) => {
    switch (type) {
      case 'video': return 'Play';
      case 'guide': return 'BookOpen';
      case 'tutorial': return 'GraduationCap';
      case 'documentation': return 'FileText';
      case 'faq': return 'HelpCircle';
      default: return 'File';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'video': return 'text-error bg-error/10';
      case 'guide': return 'text-accent bg-accent/10';
      case 'tutorial': return 'text-success bg-success/10';
      case 'documentation': return 'text-warning bg-warning/10';
      case 'faq': return 'text-secondary bg-secondary/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-sm">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-card-foreground">Centre de connaissances</h3>
          <Button
            variant="ghost"
            size="sm"
            iconName="ExternalLink"
            iconSize={16}
            onClick={() => console.log('Open full knowledge base')}
          />
        </div>

        <div className="relative mb-4">
          <Icon name="Search" size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Rechercher dans la base de connaissances..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)}
            className="w-full pl-10 pr-4 py-2 border border-border rounded-md bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
          />
        </div>

        <div className="flex space-x-2 overflow-x-auto">
          {categories?.map((category) => (
            <Button
              key={category?.id}
              variant={selectedCategory === category?.id ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setSelectedCategory(category?.id)}
              iconName={category?.icon}
              iconPosition="left"
              iconSize={14}
              className="whitespace-nowrap"
            >
              {category?.name}
            </Button>
          ))}
        </div>
      </div>
      <div className="max-h-80 overflow-y-auto">
        {filteredResources?.length === 0 ? (
          <div className="p-6 text-center">
            <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">Aucun résultat trouvé</p>
            <p className="text-sm text-muted-foreground mt-1">
              Essayez de modifier vos critères de recherche
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {filteredResources?.map((resource) => (
              <div
                key={resource?.id}
                className="p-4 hover:bg-muted/50 transition-colors duration-150 cursor-pointer"
                onClick={() => console.log('Open resource', resource?.id)}
              >
                <div className="flex items-start space-x-3">
                  <div className="flex items-center justify-center w-10 h-10 bg-accent/10 rounded-lg">
                    <Icon name={resource?.icon} size={20} className="text-accent" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <h4 className="text-sm font-medium text-card-foreground truncate">
                        {resource?.title}
                      </h4>
                      {resource?.isPopular && (
                        <span className="bg-warning/20 text-warning text-xs px-2 py-1 rounded-full">
                          Populaire
                        </span>
                      )}
                    </div>
                    
                    <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                      {resource?.description}
                    </p>
                    
                    <div className="flex items-center space-x-3">
                      <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs ${getTypeColor(resource?.type)}`}>
                        <Icon name={getTypeIcon(resource?.type)} size={12} />
                        <span className="capitalize">{resource?.type}</span>
                      </div>
                      <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                        <Icon name="Clock" size={12} />
                        <span>{resource?.duration}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="p-4 border-t border-border">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <Button
            variant="outline"
            size="sm"
            iconName="BookOpen"
            iconPosition="left"
            iconSize={14}
            onClick={() => console.log('Browse all guides')}
          >
            Tous les guides
          </Button>
          <Button
            variant="ghost"
            size="sm"
            iconName="MessageSquare"
            iconPosition="left"
            iconSize={14}
            onClick={() => console.log('Contact support')}
          >
            Contacter le support
          </Button>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeBase;